/**
 * Bank Account Form
 *
 * Create and edit bank accounts.
 */

import React, { useState } from 'react';
import {
  FormField,
  FormRow,
  FormSection,
  FormActions,
  TextInput,
  Select,
  CurrencyInput,
} from './FormComponents';
import type { BankAccount, BankAccountType } from '@business-assistant/shared/data/types';

// =============================================================================
// TYPES
// =============================================================================

interface BankAccountFormData {
  name: string;
  accountType: BankAccountType;
  bankName: string;
  iban: string;
  accountNumber: string;
  currency: string;
  initialBalance: number;
}

interface BankAccountFormProps {
  account?: BankAccount;
  onSave: (data: BankAccountFormData) => void;
  onCancel: () => void;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'checking', label: 'Conto Corrente' },
  { value: 'savings', label: 'Conto Deposito' },
  { value: 'credit', label: 'Carta di Credito' },
  { value: 'cash', label: 'Cassa' },
];

const CURRENCY_OPTIONS = [
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'USD', label: 'Dollaro USA (USD)' },
  { value: 'GBP', label: 'Sterlina (GBP)' },
  { value: 'CHF', label: 'Franco Svizzero (CHF)' },
];

// =============================================================================
// HELPERS
// =============================================================================

function formatIBAN(value: string): string {
  // Remove spaces and uppercase
  const clean = value.replace(/\s/g, '').toUpperCase();
  // Add spaces every 4 characters
  return clean.replace(/(.{4})/g, '$1 ').trim();
}

function validateIBAN(iban: string): boolean {
  const clean = iban.replace(/\s/g, '');
  // Basic Italian IBAN validation (IT + 2 check digits + 23 chars)
  if (/^IT\d{2}[A-Z]\d{10}[A-Z0-9]{12}$/i.test(clean)) {
    return true;
  }
  // Accept other IBAN formats (simplified check)
  return /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/i.test(clean);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function BankAccountForm({ account, onSave, onCancel }: BankAccountFormProps) {
  const isEditing = !!account;

  const [formData, setFormData] = useState<BankAccountFormData>({
    name: account?.name || '',
    accountType: account?.accountType || 'checking',
    bankName: account?.bankName || '',
    iban: account?.iban || '',
    accountNumber: account?.accountNumber || '',
    currency: account?.currency || 'EUR',
    initialBalance: account?.initialBalance || 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const isCash = formData.accountType === 'cash';

  // Handlers
  const updateField = <K extends keyof BankAccountFormData>(
    field: K,
    value: BankAccountFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleIBANChange = (value: string) => {
    updateField('iban', formatIBAN(value));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome conto obbligatorio';
    }

    if (!isCash) {
      if (!formData.bankName.trim()) {
        newErrors.bankName = 'Nome banca obbligatorio';
      }
      if (formData.iban && !validateIBAN(formData.iban)) {
        newErrors.iban = 'IBAN non valido';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: 'var(--bg, white)',
          borderRadius: '0.75rem',
          width: '100%',
          maxWidth: '550px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem 1.5rem',
            borderBottom: '1px solid var(--border, #e5e7eb)',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
            {isEditing ? 'Modifica Conto' : 'Nuovo Conto'}
          </h2>
          <button
            onClick={onCancel}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
            }}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          <FormSection title="Informazioni Conto">
            <FormField label="Nome Conto" required error={errors.name}>
              <TextInput
                value={formData.name}
                onChange={(v) => updateField('name', v)}
                placeholder="es. Conto Principale, Cassa Piccola..."
              />
            </FormField>

            <FormRow>
              <FormField label="Tipo Conto" required>
                <Select
                  value={formData.accountType}
                  onChange={(v) => updateField('accountType', v as BankAccountType)}
                  options={ACCOUNT_TYPE_OPTIONS}
                />
              </FormField>
              <FormField label="Valuta">
                <Select
                  value={formData.currency}
                  onChange={(v) => updateField('currency', v)}
                  options={CURRENCY_OPTIONS}
                />
              </FormField>
            </FormRow>
          </FormSection>

          {!isCash && (
            <FormSection title="Dati Bancari">
              <FormField label="Nome Banca" required={!isCash} error={errors.bankName}>
                <TextInput
                  value={formData.bankName}
                  onChange={(v) => updateField('bankName', v)}
                  placeholder="es. UniCredit, Intesa Sanpaolo..."
                />
              </FormField>

              <FormField label="IBAN" error={errors.iban}>
                <TextInput
                  value={formData.iban}
                  onChange={handleIBANChange}
                  placeholder="IT60 X054 2811 1010 0000 0123 456"
                />
              </FormField>

              <FormField label="Numero Conto">
                <TextInput
                  value={formData.accountNumber}
                  onChange={(v) => updateField('accountNumber', v)}
                  placeholder="000000123456"
                />
              </FormField>
            </FormSection>
          )}

          <FormSection title="Saldo Iniziale">
            <FormField label={isEditing ? 'Saldo Iniziale' : 'Saldo di Apertura'}>
              <CurrencyInput
                value={formData.initialBalance}
                onChange={(v) => updateField('initialBalance', v)}
              />
            </FormField>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)', marginTop: '0.25rem' }}>
              {isEditing
                ? 'Modifica solo se necessario ricalcolare il saldo.'
                : 'Inserisci il saldo attuale per iniziare a tracciare i movimenti.'}
            </p>
          </FormSection>

          <FormActions
            onCancel={onCancel}
            onSubmit={handleSubmit}
            submitLabel={isEditing ? 'Salva Modifiche' : 'Crea Conto'}
          />
        </div>
      </div>
    </div>
  );
}

export type { BankAccountFormData };
